import React, { useState } from 'react';
import ambulancePng from '../../svg/Rectangle.png';
import { makePinSvg } from '../../utils/mapMarkers';

const legendItems = [
  {
    id: 'pickup',
    label: 'Pickup Point',
    description: 'Patient location',
    imgSrc: makePinSvg('blue'),
  },
  {
    id: 'dropoff',
    label: 'Hospital / Dropoff',
    description: 'Destination',
    imgSrc: makePinSvg('red'),
  },
  {
    id: 'ambulance',
    label: 'Ambulance',
    description: 'Live vehicle',
    imgSrc: ambulancePng,
  },
  {
    id: 'direction',
    label: 'Planned Route',
    description: 'Original path',
    lineColors: ['#DC2626', '#EF4444'],
  },
  {
    id: 'route',
    label: 'Actual Route',
    description: 'Tracked path',
    lineColors: ['#1E3A8A', '#1A73E8', '#60A5FA'],
  },
  {
    id: 'timestamp',
    label: 'Time Marker',
    description: 'Position over time',
    dot: { fill: '#EF4444', stroke: '#FFFFFF' },
  },
];

const RouteLine = ({ colors, isFullscreen }) => {
  const h = isFullscreen ? 6 : 4;
  return (
    <svg
      viewBox="0 0 32 12"
      className={`w-full ${isFullscreen ? 'h-4' : 'h-3'}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {colors.length >= 3 && (
        <line x1="2" y1="6" x2="30" y2="6" stroke={colors[0]} strokeWidth={h + 4} strokeLinecap="round" opacity="0.5" />
      )}
      <line
        x1="2" y1="6" x2="30" y2="6"
        stroke={colors.length >= 3 ? colors[1] : colors[1] || colors[0]}
        strokeWidth={h}
        strokeLinecap="round"
      />
      {colors.length >= 3 && (
        <line x1="2" y1="6" x2="30" y2="6" stroke={colors[2]} strokeWidth={Math.max(h - 3, 1)} strokeLinecap="round" opacity="0.7" />
      )}
      <polygon points="27,3 31,6 27,9" fill={colors[colors.length >= 3 ? 1 : colors.length - 1]} />
    </svg>
  );
};

const TimeDot = ({ fill, stroke, isFullscreen }) => {
  const size = isFullscreen ? 18 : 14;
  return (
    <svg width={size} height={size} viewBox="0 0 18 18">
      <circle cx="9" cy="9" r="7" fill={fill} stroke={stroke} strokeWidth="2.5" />
      <circle cx="9" cy="9" r="3" fill="#FFFFFF" stroke={fill} strokeWidth="0.5" />
    </svg>
  );
};

const PremiumMapLegend = ({ isFullscreen = false, className = '' }) => {
  const [hovered, setHovered] = useState(null);

  const baseClasses = className || `absolute z-[5] ${
    isFullscreen ? 'bottom-4 left-4' : 'bottom-1 left-1'
  }`;

  return (
    <div
      className={`${baseClasses} backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border border-white/30 dark:border-gray-700/40 rounded-2xl shadow-2xl transition-all duration-300 ${
        isFullscreen ? 'px-5 py-4 min-w-[220px]' : 'px-2.5 py-2 min-w-[150px]'
      }`}
      style={{
        boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <h4
        className={`font-bold text-gray-800 dark:text-gray-100 flex items-center gap-1.5 ${
          isFullscreen ? 'text-sm mb-3' : 'text-[10px] mb-1.5'
        }`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
        Map Legend
      </h4>

      <div className={isFullscreen ? 'space-y-1.5' : 'space-y-0.5'}>
        {legendItems.map((item) => (
          <div
            key={item.id}
            className={`flex items-center rounded-xl cursor-default transition-all duration-200 ${
              isFullscreen ? 'gap-3 px-2 py-1.5' : 'gap-1.5 px-1 py-0.5'
            } ${
              hovered === item.id
                ? 'bg-gray-100/80 dark:bg-gray-800/60 scale-[1.02]'
                : 'hover:bg-gray-50/60 dark:hover:bg-gray-800/40'
            }`}
            onMouseEnter={() => setHovered(item.id)}
            onMouseLeave={() => setHovered(null)}
          >
            <div
              className={`flex-shrink-0 flex items-center justify-center ${
                isFullscreen ? 'w-7 h-7' : 'w-5 h-5'
              }`}
            >
              {item.imgSrc && (
                <img
                  src={item.imgSrc}
                  alt={item.label}
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              )}
              {item.lineColors && (
                <RouteLine colors={item.lineColors} isFullscreen={isFullscreen} />
              )}
              {item.dot && (
                <TimeDot fill={item.dot.fill} stroke={item.dot.stroke} isFullscreen={isFullscreen} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span
                className={`font-semibold text-gray-800 dark:text-gray-200 block truncate ${
                  isFullscreen ? 'text-xs' : 'text-[8px]'
                }`}
              >
                {item.label}
              </span>
              <span
                className={`text-gray-500 dark:text-gray-400 block truncate ${
                  isFullscreen ? 'text-[10px]' : 'text-[7px]'
                }`}
              >
                {item.description}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PremiumMapLegend;
